// ZONE 4 · ENGAGEMENTS — one card per engagement, and the ONLY zone on this page allowed to
// state a phase or a gate, because a card HAS an engagement id and the page does not.
//
// Each card carries: identity, its own position on the nine-position track, its own gate
// TALLY (the engine's numbers, never re-derived here), and its own route grid — every count
// in that grid scoped to that engagement, not the client's total printed twice.
//
// SUMMARISE AND ROUTE. Every question the summary provokes — which condition, whose tick,
// what detail string — is answered on the engagement surface, which owns the evaluation, the
// write and the audit trail. This page names the number and points.
//
// Two degradations, both specified rather than discovered:
//   · gate_error  — the engine threw. The message is quoted verbatim, the POSITION still
//                   paints (current_phase is a column, not an engine answer), the card still
//                   routes to where a retry lives.
//   · gate_skipped— archived. A gate guards the exit of a phase and this one is not being
//                   exited, so there is no tally: saying "0 of 4" would be a fresh evaluation
//                   of a dead row.
import { useT, useLocale } from '../i18n/index.js';
import './ClientEngagementCards.de.js';
import './ClientEngagementCards.fr.js';
import type { BriefEngagement, BriefRouteCell } from '../api/brief.js';

const ROUTE_LABEL: Record<BriefRouteCell['key'], string> = {
  documents: 'Documents',
  register: 'Register',
  agent: 'Agent',
  proposals: 'Proposals',
  contracting: 'Contracting',
  acceptance: 'Acceptance',
};

function PositionTrack({ card }: { card: BriefEngagement }) {
  return (
    <span className="client-ptrack" aria-hidden="true">
      {Array.from({ length: card.phase_count }, (_, i) => {
        const at = i === card.phase_index - 1;
        const done = i < card.phase_index - 1;
        return <i key={i} className={`client-ptick${at ? ' client-ptick--at' : done ? ' client-ptick--done' : ''}`} />;
      })}
    </span>
  );
}

interface Props {
  engagements: BriefEngagement[];
  onNewEngagement: () => void;
  /** Open sets the current engagement and lands on Lifecycle. */
  onOpen: (id: number) => void;
  onRouteCell: (engagementId: number, cell: BriefRouteCell) => void;
}

export default function ClientEngagementCards({ engagements, onNewEngagement, onOpen, onRouteCell }: Props) {
  const t = useT();
  const { fmtDate, fmtNum } = useLocale();
  const live = engagements.filter((e) => e.archived_at === null);
  const archived = engagements.filter((e) => e.archived_at !== null);

  return (
    <section className="zone" id="engzone">
      <div className="zone-head">
        <span className="u-label">{t('Engagements')}</span>
        <span className="zone-count voice-figure">
          {live.length ? t('{n} active', { n: live.length }) : t('none active')}
          {archived.length ? ` · ${t('{n} archived', { n: archived.length })}` : ''}
        </span>
        <button type="button" className="zone-route" onClick={onNewEngagement}>{t('New engagement')}</button>
      </div>

      {!engagements.length ? (
        <p className="zone-empty">
          {t('No engagement on this client yet — so no phase, no gate, and nothing on this page claims one.')}{' '}
          <button type="button" className="btn btn-sm" style={{ marginLeft: 8 }} onClick={onNewEngagement}>
            {t('New engagement')}
          </button>
        </p>
      ) : (
        engagements.map((card) => {
          const isLive = card.archived_at === null;
          return (
            <div className={`client-eng${isLive ? '' : ' client-eng--arch'}`} key={card.id}>
              <div className="client-eng-h">
                <span className="src-ref" style={{ color: 'var(--ink-3)' }}>{card.code}</span>
                <h3>{card.name}</h3>
                <span className="client-chip">
                  {isLive ? card.phase_label : t('archived {date}', { date: fmtDate(card.archived_at) })}
                </span>
                <div className="client-eng-figs">
                  <div className="client-eng-fig">
                    <div className="client-eng-fig-n voice-figure">
                      {card.estimate_value
                        ? fmtNum(card.estimate_value)
                        : <span className="n-none">—</span>}
                    </div>
                    <div className="client-eng-fig-l">{t('{currency} estimate', { currency: card.currency })}</div>
                  </div>
                  <div className="client-eng-fig">
                    <div className="client-eng-fig-n voice-figure">
                      {card.kickoff_date ? fmtDate(card.kickoff_date) : <span className="n-none">—</span>}
                    </div>
                    <div className="client-eng-fig-l">{t('kickoff')}</div>
                  </div>
                </div>
              </div>

              {/* position on the left, this engagement's gate on the right */}
              <div className="client-lc">
                <div className="client-lc-l">
                  <PositionTrack card={card} />
                  <div className="client-lc-cap voice-figure">
                    {t('phase')} <b>{card.phase_index}</b> {t('of')} <b>{card.phase_count}</b>{' '}
                    <span className="client-ph">{card.phase_label}</span>
                  </div>
                </div>

                {card.gate_skipped ? (
                  <div className="client-lc-r">
                    <span className="client-lc-title">{t('no gate')}</span>
                    <span className="client-lc-tally">{card.gate_skipped}</span>
                    <button type="button" className="zone-route" style={{ marginLeft: 0 }} onClick={() => onOpen(card.id)}>
                      {t('Open {code}', { code: card.code })} →
                    </button>
                  </div>
                ) : card.gate_error ? (
                  <div className="client-lc-r">
                    <span className="client-lc-title" style={{ color: 'var(--err)' }}>{t('gate engine did not answer')}</span>
                    {/* the engine's message, verbatim — a failure is quoted, never paraphrased */}
                    <span className="client-lc-tally n-unknown" title={card.gate_error}>{t('tally not evaluable')}</span>
                    <button type="button" className="zone-route" style={{ marginLeft: 0 }} onClick={() => onOpen(card.id)}>
                      {t('Lifecycle')} →
                    </button>
                  </div>
                ) : card.gate ? (
                  <div className="client-lc-r">
                    <span className="client-lc-gate src-ref">{card.gate.gate}</span>
                    <span className="client-lc-title">{card.gate.title}</span>
                    <span className="client-pips" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${Math.max(1, card.gate.pips.length)}, 1fr)` }}>
                      {card.gate.pips.length
                        ? card.gate.pips.map((pip, i) => (
                            <i
                              key={`${pip.label}-${i}`}
                              className={`client-pip ${pip.met ? 'client-pip--on' : 'client-pip--off'}`}
                              title={`${pip.label} — ${pip.detail}`}
                            />
                          ))
                        : <i className="client-pip" />}
                    </span>
                    {/* a tally is COMPUTED — it carries no claim-mark (LAW 6) */}
                    <span className="client-lc-tally voice-figure">
                      <b>{card.gate.auto_met}</b>/{card.gate.auto_total} {t('automatic')} ·{' '}
                      <b>{card.gate.manual_met}</b>/{card.gate.manual_total} {t('manual')}
                    </span>
                    <button type="button" className="zone-route" style={{ marginLeft: 0 }} onClick={() => onOpen(card.id)}>
                      {t('Lifecycle')} →
                    </button>
                  </div>
                ) : (
                  <div className="client-lc-r">
                    <span className="client-lc-tally n-unknown" title={t('No gate was evaluated for this engagement.')}>
                      {t('gate not evaluable')}
                    </span>
                  </div>
                )}
              </div>

              {isLive && (
                <div className="client-routes">
                  {card.routes.map((cell) => (
                    <button type="button" className="client-route" key={cell.key} onClick={() => onRouteCell(card.id, cell)}>
                      <div className={`client-route-n voice-figure${cell.count ? '' : ' client-route-n--zero'}`}>{cell.count}</div>
                      <div className="client-route-l">{t(ROUTE_LABEL[cell.key])} →</div>
                      <div className="client-route-q">{cell.qualifier}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}
    </section>
  );
}
